import React from 'react';
import {useForm} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Modal from './common/Modal';
import InputField from './ui/InputField';
import {SelectFieldHookForm} from './ui/SelectField';
import {userType} from '../utils/constants';
import {createUser} from '../api/auth';
import {invalidateQueryV2} from '../services/reactQuery';

const CreateUserModal = ({isOpen, onClose}) => {
  const {handleSubmit, register, control, reset} = useForm({
    mode: 'all',
    defaultValues: {
      role: userType[1], // Default to invigilator
    },
  });

  const createUserMutation = useMutation(createUser, {
    onSuccess: () => {
      toast.success('User created successfully');
      onClose();
      reset();
      invalidateQueryV2(['users']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Something went wrong');
    },
  });

  const onSubmit = (data) => {
    const payload = {
      ...data,
      role:
        typeof data.role === 'object' && data.role !== null
          ? data.role.value
          : data.role,
    };
    createUserMutation.mutate(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New User">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">First Name</label>
            <InputField
              {...register('firstName', {required: 'First name is required'})}
              placeholder="Enter first name"
            />
          </div>
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Last Name</label>
            <InputField
              {...register('lastName', {required: 'Last name is required'})}
              placeholder="Enter last name"
            />
          </div>
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Email</label>
            <InputField
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              placeholder="Enter email"
            />
          </div>
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Password</label>
            <InputField
              type="password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              placeholder="Enter password"
            />
          </div>
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Phone</label>
            <InputField
              type="tel"
              {...register('phone', {required: 'Phone number is required'})}
              placeholder="Enter phone number"
            />
          </div>
          <div className="flex flex-col col-span-2">
            <label className="font-semibold mb-2.5">Role</label>
            <SelectFieldHookForm
              control={control}
              name="role"
              options={userType}
              rules={{required: 'Role is required'}}
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createUserMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-main  border border-transparent rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 "
          >
            {createUserMutation.isLoading ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserModal;
